import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getGetMyRecoveryAccessQueryKey, useActivateMyRecoveryAccess, useEndMyRecoveryAccess, useGetMyRecoveryAccess } from '@cdf/api-client-react';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const DURATIONS = [15, 30, 45, 60];

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Recovery access could not be changed. Please try again.';
}

/**
 * Emergency recovery for appointed custodians. Only rendered for custodians;
 * everyone else sees nothing. The server enforces every rule.
 */
export function RecoveryAccessPanel() {
  const access = useTenantEntity();
  const queryClient = useQueryClient();
  const status = useGetMyRecoveryAccess({ query: { queryKey: getGetMyRecoveryAccessQueryKey(), refetchInterval: 60000 } });
  const activate = useActivateMyRecoveryAccess();
  const end = useEndMyRecoveryAccess();
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('60');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const data = status.data;
  if (!data?.isCustodian) return null;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetMyRecoveryAccessQueryKey() });
    await access.refresh();
  };
  const start = async () => {
    setError(null); setNotice(null);
    try {
      await activate.mutateAsync({ data: { reason: reason.trim(), durationMinutes: Number(duration) } });
      setReason('');
      setNotice('Emergency recovery access is on. It switches off automatically when the time is up.');
      await refresh();
    } catch (reasonError) { setError(errorMessage(reasonError)); }
  };
  const stop = async (id: string) => {
    setError(null); setNotice(null);
    try {
      await end.mutateAsync({ id, data: { reason: 'Recovery access ended by the custodian.' } });
      setNotice('Emergency recovery access has ended.');
      await refresh();
    } catch (reasonError) { setError(errorMessage(reasonError)); }
  };
  const busy = activate.isPending || end.isPending;

  return <section className="space-y-3 border-t pt-5" aria-labelledby="recovery-access-title">
    <div className="space-y-1">
      <h3 id="recovery-access-title" className="font-medium">Emergency recovery</h3>
      <p className="text-sm text-muted-foreground">You are a recovery custodian. Use this only when no administrator can approve access. It gives you Platform Administrator access for up to {data.maxDurationMinutes} minutes and is recorded in the audit log.</p>
    </div>
    {data.session ? <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 p-3">
      <p className="text-sm"><strong>Recovery access is on</strong> until {new Date(data.session.expiresAt).toLocaleTimeString()}.</p>
      <Button variant="outline" disabled={busy} onClick={() => void stop(data.session!.id)}>End now</Button>
    </div> : !data.ready ? <p role="status" className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
      Recovery is not available yet: at least {data.minimumCustodians} custodians must be appointed ({data.activeCustodians} so far).
    </p> : <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); void start(); }}>
      <div className="space-y-2"><Label htmlFor="recovery-reason">What needs recovering? (at least 10 characters)</Label>
        <Textarea id="recovery-reason" required minLength={10} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} /></div>
      <div className="space-y-2"><Label htmlFor="recovery-duration">Duration</Label>
        <Select value={duration} onValueChange={setDuration}><SelectTrigger id="recovery-duration"><SelectValue /></SelectTrigger>
          <SelectContent>{DURATIONS.filter((minutes) => minutes >= data.minDurationMinutes && minutes <= data.maxDurationMinutes)
            .map((minutes) => <SelectItem key={minutes} value={String(minutes)}>{minutes} minutes</SelectItem>)}</SelectContent></Select></div>
      <Button type="submit" variant="destructive" disabled={busy || reason.trim().length < 10}>Switch on recovery access</Button>
    </form>}
    {notice && <p role="status" className="text-sm">{notice}</p>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
  </section>;
}
